import {
  type AppError,
  InvalidTokenError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {DEFAULT_API_VERSION} from '../../../config/apiVersion';
import {cookieApiRefreshTokenKey} from '../../../server/token';
import {refreshAccessTokenWithLock} from './refreshAccessTokenWithLock';

export type HandleRefreshAccessTokenArgs<T> = {
  cookies: Partial<Record<string, string>>;
  request: (accessToken: string) => Promise<Result<T, AppError>>; // 元のリクエストを再実行する関数
  version?: string;
};

export const SESSION_EXPIRED_RETURN_VALUE = {
  ok: false,
  error: InvalidTokenError,
} as const;

// Tは元のリクエストの戻り値の型
export const handleRefreshAccessToken = async <T>({
  cookies,
  request,
  version = DEFAULT_API_VERSION,
}: HandleRefreshAccessTokenArgs<T>): Promise<
  ResultWithOauthToken<T, AppError>
> => {
  const refreshToken = cookies[cookieApiRefreshTokenKey];
  if (!refreshToken) {
    return SESSION_EXPIRED_RETURN_VALUE;
  }

  // クライアントサイドではCookie保存とアプリへの送信もこの中で行われる
  const refreshResult = await refreshAccessTokenWithLock({
    refreshToken,
    version,
  });
  if (!refreshResult.ok) {
    if (refreshResult.error === InvalidTokenError) {
      return SESSION_EXPIRED_RETURN_VALUE;
    }

    // 5xxや通信断でセッション切れUIを出すとユーザーがログアウトされてしまう
    return {
      ok: false,
      error: refreshResult.error,
    };
  }

  const {accessToken: newAccessToken, refreshToken: newRefreshToken} =
    refreshResult.value;

  const newRequestResult = await request(newAccessToken);
  return {
    ...newRequestResult,
    accessToken: newAccessToken,
    refreshToken: newRefreshToken,
  };
};
